import { useEffect } from "react";
import Lenis from "lenis";
import { motion } from "framer-motion";
import { GraduationCap, Award, Scale, BookOpen } from "lucide-react";
import Navbar from "@/components/landing/Navbar";
import Footer from "@/components/landing/Footer";
import { Reveal } from "@/components/motion/Reveal";
import { ApplicationForm } from "@/components/careers/ApplicationForm";
import { setLenis } from "@/lib/scroll";

const REQUIREMENTS = [
  { icon: Award, title: "Media esami ≥ 27/30", text: "Selezioniamo profili ad alto rendimento accademico, con una media ponderata pari o superiore a 27/30." },
  { icon: GraduationCap, title: "Ultimo anno o neolaureati", text: "Iscrizione all'ultimo anno del corso di laurea magistrale in Giurisprudenza oppure laurea conseguita di recente." },
  { icon: Scale, title: "Tirocinio Forense Anticipato", text: "Percorso in convenzione universitaria per anticipare la pratica durante l'ultimo anno di studi." },
  { icon: BookOpen, title: "Pratica Post-Laurea", text: "Formazione strutturata in Diritto Civile, Infortunistica, Tributario e Recupero Crediti presso le nostre sedi." },
];

const Careers = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    setLenis(lenis);
    let raf;
    const loop = (time) => {
      lenis.raf(time);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      setLenis(null);
    };
  }, []);

  return (
    <div className="bg-ivory" data-testid="careers-page">
      <Navbar />

      <section className="relative overflow-hidden bg-navy pt-40 pb-24 sm:pt-48 sm:pb-32" data-testid="careers-hero">
        <div className="absolute inset-0 bg-navy-texture opacity-70" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(163,172,185,0.16),transparent_55%)]" />
        <div className="relative z-10 mx-auto max-w-7xl px-5 sm:px-8">
          <motion.p initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="inline-flex items-center gap-3 text-[11px] sm:text-xs font-semibold uppercase tracking-[0.35em] text-gold">
            <span className="h-px w-10 bg-gold/70" /> Lavora con Noi
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
            className="mt-6 max-w-4xl font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-white"
            data-testid="careers-title"
          >
            Opportunità per <span className="font-display italic text-gold">Studenti</span> e Praticanti
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.4 }} className="mt-7 max-w-2xl text-base sm:text-lg leading-relaxed text-slate-300" data-testid="careers-description">
            Lo Studio Legale seleziona candidati ad alto rendimento per il Tirocinio Forense Anticipato (in convenzione universitaria) e la pratica post-laurea.
          </motion.p>
        </div>
      </section>

      <section className="bg-pearl py-20 sm:py-28" data-testid="careers-requirements">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal>
            <p className="text-[11px] font-semibold uppercase tracking-[0.3em] text-gold-dark">Requisiti chiave</p>
            <h2 className="mt-3 font-serif text-3xl sm:text-4xl text-navy">Chi stiamo cercando</h2>
          </Reveal>
          <div className="mt-12 grid gap-px bg-navy/10 border border-navy/10 sm:grid-cols-2 lg:grid-cols-4">
            {REQUIREMENTS.map((r, i) => (
              <Reveal key={r.title} delay={i * 0.1}>
                <div className="group h-full bg-white p-7 transition-colors duration-500 hover:bg-navy" data-testid={`requirement-card-${i}`}>
                  <span className="flex h-11 w-11 items-center justify-center border border-gold/40 bg-gold/10 text-gold-dark transition-colors group-hover:text-gold">
                    <r.icon className="h-5 w-5" />
                  </span>
                  <h3 className="mt-6 font-serif text-xl text-navy transition-colors group-hover:text-white">{r.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-slate-600 transition-colors group-hover:text-slate-300">{r.text}</p>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.2}>
            <div className="mt-10 grid gap-6 border border-gold/40 bg-white p-7 sm:p-9 lg:grid-cols-[auto_1fr]" data-testid="careers-legal-reference">
              <span className="flex h-12 w-12 shrink-0 items-center justify-center border border-gold/40 bg-gold/10 text-gold-dark">
                <Scale className="h-5 w-5" />
              </span>
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.3em] text-gold-dark">Riferimento normativo</p>
                <h3 className="mt-2 font-serif text-2xl text-navy">Semestre anticipato</h3>
                <p className="mt-3 leading-relaxed text-slate-600">
                  Il tirocinio è compatibile con gli ultimi mesi di università: gli studenti possono svolgere il tirocinio forense presso lo Studio
                  durante gli ultimi mesi del percorso universitario. L'<strong className="text-navy">articolo 5 del D.M. 17 marzo 2016, n. 70</strong> disciplina
                  lo svolgimento del tirocinio anticipato con modalità idonee a garantire l'effettivo e puntuale svolgimento della pratica forense.
                </p>
                <p className="mt-3 text-sm text-slate-500">
                  Lo Studio concorda con ciascun tirocinante un piano di presenza compatibile con gli impegni universitari, nel rispetto della disciplina vigente.
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section id="candidatura" className="bg-ivory py-20 sm:py-28" data-testid="careers-form-section">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <div className="grid gap-12 lg:grid-cols-5">
            <Reveal className="lg:col-span-2">
              <p className="text-[11px] font-semibold uppercase tracking-[0.3em] text-gold-dark">Modulo di Candidatura</p>
              <h2 className="mt-3 font-serif text-3xl sm:text-4xl text-navy">Invia il tuo profilo</h2>
              <p className="mt-5 leading-relaxed text-slate-600">
                Compila il modulo e allega il tuo curriculum. Ogni candidatura viene esaminata personalmente dall'Avv. Napolitano: i profili ritenuti idonei saranno ricontattati ai recapiti indicati per un colloquio conoscitivo.
              </p>
              <div className="mt-8 bg-navy p-6">
                <p className="font-display text-lg italic text-white">«Il talento si riconosce dal rigore.»</p>
                <p className="mt-2 text-sm text-slate-300">Le candidature restano valide per 12 mesi e sono trattate nel pieno rispetto della normativa GDPR.</p>
              </div>
            </Reveal>
            <Reveal delay={0.15} className="lg:col-span-3">
              <ApplicationForm />
            </Reveal>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Careers;
